import React, { Suspense, useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import { fitScale } from "@/features/session-diagnostics/components/ReplayStage";
import { useMediaQuery } from "@/features/session-diagnostics/hooks/use-desktop-fit";
import { Link } from "react-router";
import { DailyIcon } from "@/features/daily/components/DailyIcon";
import { liveSessionSlots, type LiveSessionProgressOutcome, type LiveSessionSlotsAction } from "@/features/daily/components/command/live-session-queue";
import { sessionStreamDeps } from "@/features/session-diagnostics/access/SessionDiagnosticsRoute";
import { sessionDiagnosticsApi, sessionFilters } from "@/features/session-diagnostics/api/session-diagnostics.api";
import { SegmentPollContext, type SegmentPollStore, segmentPollStore } from "@/features/session-diagnostics/hooks/use-segment-poll";
import { useSessionList } from "@/features/session-diagnostics/hooks/use-session-list";

export type LiveSessionPlayer = React.ComponentType<{ segmentId: string; width: number }>;

export const PHONE_WIDTH = 194;
// Longer than the 10 s list cadence on purpose: a read slower than one refresh must survive it (it can still hand over).
export const PROGRESS_TIMEOUT_MS = 25_000;
const PHONE_PADDING = 5;
const PHONE_NOTCH = 16;
// The phone's own height on a desktop board: the player's width drawing a 390×844 screen, plus notch and frame.
// The command center fits the screen height only on tall desktop screens (owner ruling); the phone scales there.
export const BOARD_FIT_QUERY = "(min-width: 1200px) and (min-height: 900px)";
// «32 inches and up» (owner): CSS cannot see inches, so a very large viewport stands in for it. 2400 × 1100 CSS px is
// reached by a 32″ 4K monitor at 150 % (2560 × 1440) or a 32″ 1440p at 100 %, and not by a 24″/27″ 1080p or a laptop.
export const WALL_QUERY = "(min-width: 2400px) and (min-height: 1100px)";
// On the tall board the phone fills its row; it may grow up to 2.5 × its own size (a 3840 × 2160 row needs ≈ 2.34).
export const PHONE_MAX_SCALE = 2.5;
export const PHONE_HEIGHT = Math.round(((PHONE_WIDTH - 2 * PHONE_PADDING) * 844) / 390) + PHONE_NOTCH + 2 * PHONE_PADDING;
const TITLE = "Retransmisión de la pantalla del usuario";

const Screen: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="daily-cc-phone__frame">
        <div className="daily-cc-phone__screen">
            <div className="daily-cc-phone__notch" aria-hidden="true"><span /></div>
            {children}
        </div>
    </div>
);

const Message: React.FC<{ icon: string; title: string; text: string | null }> = ({ icon, title, text }) => (
    <div className="daily-cc-phone__message" role="status">
        <DailyIcon name={icon} />
        <span className="daily-cc-phone__message-title">{title}</span>
        {text === null ? null : <span className="daily-cc-phone__message-text">{text}</span>}
    </div>
);

const Casting: React.FC = () => (
    <div className="daily-cc-phone__message">
        <DailyIcon name="cast" />
        <span className="daily-cc-phone__message-text">{TITLE}</span>
    </div>
);

// vb-metrics#38 owner ruling: on a tall desktop board the phone keeps its aspect and fills the height of its row (a
// transform on a wrapper, like the session stage), shrinking or growing up to PHONE_MAX_SCALE. The list summary carries no
// recorded viewport, so the aspect is the phone's own 194 × PHONE_HEIGHT.
const PhoneFit: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const box = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number | null>(null);
    useLayoutEffect(() => {
        const node = box.current;
        if (node === null || typeof ResizeObserver !== "function") return;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) setHeight(entry.contentRect.height);
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);
    const scale = height === null ? 1 : fitScale({ availableW: Number.POSITIVE_INFINITY, availableH: height, recordedW: PHONE_WIDTH, recordedH: PHONE_HEIGHT, max: PHONE_MAX_SCALE });
    return (
        <div ref={box} className="daily-cc-phone__fit" data-testid="phone-fit">
            <div className="daily-cc-phone__reserve" style={{ width: PHONE_WIDTH * scale, height: PHONE_HEIGHT * scale }}>
                <div className="daily-cc-phone__scaled" data-testid="phone-scaled" style={{ width: PHONE_WIDTH, height: PHONE_HEIGHT, transform: `translateX(-50%) scale(${scale})` }}>
                    {children}
                </div>
            </div>
        </div>
    );
};

const hrefOf = (segmentId: string, workspaceId: string): string =>
    `/diagnostic/sessions/${segmentId}?${sessionFilters.toSearch({ ...sessionFilters.empty, workspaceId })}`;

type Props = { workspaceId: string; Player: LiveSessionPlayer; onStreamingChange: (streaming: boolean) => void };

type Check = { segmentId: string; since: number; round: number };
type Dispatch = (action: LiveSessionSlotsAction) => void;

// At most one progress read per list refresh for each followed session. A read still in flight is left to finish (a
// slow answer must still be able to hand over) and no second one is sent for the same session. Each slot has its own.
const ProgressReader: React.FC<{ check: Check | null; followedId: string | null; workspaceId: string; dispatch: Dispatch }> = ({ check, followedId, workspaceId, dispatch }) => {
    const pending = useRef<{ segmentId: string; controller: AbortController; timer: ReturnType<typeof setTimeout> } | null>(null);
    useEffect(() => {
        if (check === null) return;
        const inFlight = pending.current;
        if (inFlight !== null && inFlight.segmentId === check.segmentId) return;
        const controller = new AbortController();
        const release = (): void => {
            if (pending.current !== null && pending.current.controller === controller) {
                clearTimeout(pending.current.timer);
                pending.current = null;
            }
        };
        const settle = (outcome: LiveSessionProgressOutcome): void => {
            if (controller.signal.aborted === false) dispatch({ kind: "progress", segmentId: check.segmentId, outcome });
        };
        // A hung read must not hold the slot forever: past the timeout it counts as a failed read and is cancelled.
        const timer = setTimeout(() => {
            settle({ kind: "failed" });
            controller.abort();
            release();
        }, PROGRESS_TIMEOUT_MS);
        pending.current = { segmentId: check.segmentId, controller, timer };
        sessionDiagnosticsApi.progress(workspaceId, check.segmentId, check.since, controller.signal)
            .then((progress) => settle(progress === null ? { kind: "missing" } : { kind: "ok", progress }))
            .catch(() => settle({ kind: "failed" }))
            .finally(release);
    }, [check, workspaceId, dispatch]);

    // Only a change of the followed session (or leaving the view) cancels the read in flight.
    useEffect(() => () => {
        if (pending.current !== null) {
            clearTimeout(pending.current.timer);
            pending.current.controller.abort();
        }
        pending.current = null;
    }, [followedId, workspaceId]);
    return null;
};

const LivePhone: React.FC<Props> = ({ workspaceId, Player, onStreamingChange }) => {
    const list = useSessionList(workspaceId);
    const slotCount = useMediaQuery(WALL_QUERY) ? 2 : 1;
    const [queue, dispatch] = useReducer(liveSessionSlots.reduce, slotCount, liveSessionSlots.initial);
    const latest = list.state.status === "ok" ? list.state.latest : null;

    useEffect(() => {
        dispatch({ kind: "slots", count: slotCount });
    }, [slotCount]);
    useEffect(() => {
        if (latest !== null) dispatch({ kind: "list", sessions: latest });
    }, [latest]);

    // vb-metrics#38: the phone takes board space only while a session is being followed; the queue above keeps
    // running so it appears as soon as one starts.
    const desktopFit = useMediaQuery(BOARD_FIT_QUERY);
    const followed = list.state.status === "ok" ? queue.slots.flatMap((slot) => (slot.followed === null ? [] : [slot.followed])) : [];
    const streaming = followed.length > 0;
    // Before paint: the board's column appears in the same frame as the phone, never a frame late.
    useLayoutEffect(() => {
        onStreamingChange(streaming);
    }, [streaming, onStreamingChange]);
    useLayoutEffect(() => () => onStreamingChange(false), [onStreamingChange]);

    const readers = queue.slots.map((slot, index) => (
        <ProgressReader key={index} check={slot.check} followedId={slot.followed === null ? null : slot.followed.segmentId} workspaceId={workspaceId} dispatch={dispatch} />
    ));
    if (followed.length === 0) return <>{readers}</>;
    const waiting = liveSessionSlots.waiting(queue);
    const phones = followed.map((session, index) => {
        const label = followed.length === 1 ? "Abrir la sesión" : `Abrir la sesión ${index + 1} de ${followed.length}`;
        const phone = (
            <Link to={hrefOf(session.segmentId, workspaceId)} className="daily-cc-phone__link" title={label} aria-label={label}>
                <Screen>
                    <div className="daily-cc-phone__live">
                        <span aria-hidden="true" />
                        EN VIVO
                        {index === 0 && waiting > 0 ? <span className="daily-cc-phone__waiting">{`+${waiting} en espera`}</span> : null}
                    </div>
                    {session.replayAvailable ? (
                        <Suspense fallback={<Casting />}>
                            <Player segmentId={session.segmentId} width={PHONE_WIDTH - 2 * PHONE_PADDING} />
                        </Suspense>
                    ) : (
                        <Message icon="cast" title="Sin replay para esta sesión (no implica ausencia de actividad)" text={null} />
                    )}
                </Screen>
            </Link>
        );
        return desktopFit ? <PhoneFit key={session.segmentId}>{phone}</PhoneFit> : <React.Fragment key={session.segmentId}>{phone}</React.Fragment>;
    });
    return (
        <>
            {readers}
            <section className="daily-cc-phone" aria-label={TITLE} data-phones={followed.length} style={desktopFit ? undefined : { width: PHONE_WIDTH }}>
                {phones}
            </section>
        </>
    );
};

// Its own poll stream: the command center is outside the sessions route that owns the shared one.
export const LiveSessionPhone: React.FC<Props> = ({ workspaceId, Player, onStreamingChange }) => {
    const [store, setStore] = useState<SegmentPollStore | null>(null);
    useEffect(() => {
        const created = segmentPollStore.create(sessionStreamDeps(workspaceId));
        setStore(created);
        return () => created.dispose();
    }, [workspaceId]);
    return store === null ? null : (
        <SegmentPollContext.Provider value={store}>
            <LivePhone workspaceId={workspaceId} Player={Player} onStreamingChange={onStreamingChange} />
        </SegmentPollContext.Provider>
    );
};
